/**
 * Suche in der Feldauswahl (#1245, Teil 47).
 *
 * Eine Notariatsmitarbeiterin tippt »Name des ersten Erwerbers«, »zuständiges Grundbuchamt« oder
 * »UR-Nr.« — nicht `LegalClientArray[LegalRole=Käufer,occurrence=1].NameVoll`. Gesucht wird
 * deshalb über das, was der Katalog fachlich über ein Feld sagt: Name, Ebene (»Käufer 1«,
 * »Grundbuchblatt 1 › Bestandsposten 1«), Bereich, Suchbegriffe und Synonyme — und zuletzt der
 * Vorschauwert, damit auch findet, wer nur weiß, was im Dokument stehen soll.
 *
 * Pfad, Feldname und ArnoTop-Namen (`technicalSearch`) werden nur durchsucht, wenn die
 * technischen Felder eingeschaltet sind. Wer »Court«, »Array« oder »Id« tippt, findet in der
 * normalen Auswahl deshalb nichts — und bekommt gesagt, dass es technische Treffer gäbe.
 *
 * Reihenfolge: erst die Felder, die die meisten Suchwörter treffen; darunter Treffer im Namen
 * vor Treffern in den Synonymen, vor der Ebene, vor dem Vorschauwert.
 */

export interface SearchableField {
  path: string;
  label: string;
  group: string;
  subgroup?: string | null;
  search?: string[];
  technicalSearch?: string[];
  sample?: string | null;
  visibility?: 'normal' | 'expert';
}

/** Kleinschreibung, ohne Akzente (ä → a), ß → ss — für beide Seiten gleich. */
export const foldText = (text: string): string =>
  (text ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/ß/g, 'ss')
    .toLowerCase();

const words = (text: string): string[] =>
  foldText(text)
    .split(/[^a-z0-9]+/)
    .filter(Boolean);

/** Wörter, die eine Suche nicht enger machen. »alle« und »nicht« tragen Bedeutung und bleiben. */
const STOPWORDS = new Set([
  'der',
  'die',
  'das',
  'des',
  'dem',
  'den',
  'ein',
  'eine',
  'einer',
  'eines',
  'einem',
  'einen',
  'von',
  'vom',
  'zu',
  'zum',
  'zur',
  'mit',
  'und',
  'oder',
  'im',
  'in',
  'am',
  'an',
  'auf',
  'fur',
  'als',
  'bei',
  'nach',
  'aus',
  'uber',
  'wie',
]);

/** »ersten Erwerbers« → Erwerber 1. */
const ORDINALS: Array<[RegExp, string]> = [
  [/^erst(e|er|es|en|em)?$/, '1'],
  [/^zweit(e|er|es|en|em)?$/, '2'],
  [/^dritt(e|er|es|en|em)?$/, '3'],
  [/^viert(e|er|es|en|em)?$/, '4'],
  [/^funft(e|er|es|en|em)?$/, '5'],
];

/** Die Suchwörter, je mit einer Grundform ohne gängige Endung (»Erwerbers« → »erwerber«). */
export const queryTokens = (query: string): string[][] =>
  words(query)
    .filter(word => !STOPWORDS.has(word))
    .map(word => {
      const ordinal = ORDINALS.find(([pattern]) => pattern.test(word));
      if (ordinal) {
        return [ordinal[1]];
      }
      const stem = word.length > 4 ? word.replace(/(es|en|s|n|e)$/, '') : word;
      return stem !== word ? [word, stem] : [word];
    });

/**
 * 3 = das getippte Wort ganz, 2 = seine Grundform ganz oder ein Wortanfang, 1 = irgendwo im Text,
 * 0 = kein Treffer. Kurze Wörter (»Id«, »BV«) nur ganz. »Herrn« trifft damit die gebeugte Form vor
 * der ungebeugten, die nur »Herr« kennt.
 */
const matchLevel = (variants: string[], text: string): number => {
  let best = 0;
  const textWords = words(text);
  const folded = foldText(text);
  variants.forEach((token, index) => {
    if (textWords.includes(token)) {
      best = Math.max(best, index === 0 ? 3 : 2);
      return;
    }
    if (token.length <= 2) {
      return;
    }
    if (textWords.some(word => word.startsWith(token))) {
      best = Math.max(best, 2);
    } else if (folded.includes(token)) {
      best = Math.max(best, 1);
    }
  });
  return best;
};

/** Punkte je Fundstelle: [ganzes Wort, Wortanfang, irgendwo]. */
const WEIGHTS = {
  label: [100, 80, 50],
  search: [60, 50, 25],
  context: [45, 40, 20],
  technical: [30, 25, 15],
  sample: [12, 10, 10],
} as const;

const scoreIn = (
  variants: string[],
  texts: Array<string | null | undefined>,
  weights: readonly [number, number, number] | readonly number[]
): number => {
  let best = 0;
  for (const text of texts) {
    if (!text) {
      continue;
    }
    const level = matchLevel(variants, text);
    if (level > 0) {
      best = Math.max(best, weights[3 - level]);
    }
  }
  return best;
};

/** Wo das Feld steht, als eine Zeile: »Beteiligte › Käufer 2 › Anschrift«. */
export const fieldContext = (
  field: Pick<SearchableField, 'group' | 'subgroup'>
): string => [field.group, field.subgroup].filter(Boolean).join(' › ');

export interface RankOptions {
  /** Technische Felder und technische Suchbegriffe einbeziehen. */
  technical?: boolean;
  /** Höchstens so viele Treffer. */
  limit?: number;
}

/**
 * Die Felder zu einer Suche, bestes zuerst. Ohne Suchtext: alle (fachlichen) Felder in der
 * Reihenfolge des Katalogs.
 */
export function rankFields<T extends SearchableField>(
  fields: T[],
  query: string,
  { technical = false, limit }: RankOptions = {}
): T[] {
  const candidates = technical
    ? fields
    : fields.filter(field => field.visibility !== 'expert');
  const tokens = queryTokens(query);
  if (tokens.length === 0) {
    return limit ? candidates.slice(0, limit) : candidates;
  }
  const whole = foldText(query).trim();
  const scored = candidates.map((field, index) => {
    let matched = 0;
    let score = 0;
    for (const variants of tokens) {
      const best = Math.max(
        scoreIn(variants, [field.label], WEIGHTS.label),
        scoreIn(variants, field.search ?? [], WEIGHTS.search),
        scoreIn(variants, [field.subgroup, field.group], WEIGHTS.context),
        technical
          ? scoreIn(variants, field.technicalSearch ?? [], WEIGHTS.technical)
          : 0,
        scoreIn(variants, [field.sample], WEIGHTS.sample)
      );
      if (best > 0) {
        matched += 1;
        score += best;
      }
    }
    // Der Name beginnt mit der ganzen Suche (als Wort, nicht »Namen« für »Name«).
    const label = words(field.label).join(' ');
    const asked = words(query).join(' ');
    if (label === asked) {
      score += 50;
    } else if (label.startsWith(`${asked} `)) {
      score += 30;
    }
    // In den technischen Feldern: der Feldname genau so, wie getippt.
    if (technical && foldText(field.path.split('.').pop() ?? '') === whole) {
      score += 40;
    }
    return { field, index, matched, score };
  });
  const most = Math.max(0, ...scored.map(entry => entry.matched));
  if (most === 0) {
    return [];
  }
  const ranked = scored
    .filter(entry => entry.matched === most)
    // Fachliche Felder immer vor technischen, dann nach Punkten, dann wie im Katalog.
    .sort(
      (a, b) =>
        Number(a.field.visibility === 'expert') -
          Number(b.field.visibility === 'expert') ||
        b.score - a.score ||
        a.index - b.index
    )
    .map(entry => entry.field);
  return limit ? ranked.slice(0, limit) : ranked;
}

/** Wie viele technische Felder die Suche träfe — für den Hinweis bei null fachlichen Treffern. */
export const technicalHits = (
  fields: SearchableField[],
  query: string
): number =>
  rankFields(fields, query, { technical: true }).filter(
    field => field.visibility === 'expert'
  ).length;
